/**
 * OnboardingPickerTable Component
 *
 * Displays a searchable table of folders and onboardings.
 * Folders are displayed first, then onboardings.
 */

import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import type { OnboardingPickerTableProps } from './OnboardingPicker.types';

/**
 * Format date for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * OnboardingPickerTable Component
 *
 * @example
 * ```tsx
 * <OnboardingPickerTable
 *   items={items}
 *   loading={false}
 *   error={null}
 *   searchQuery=""
 *   onSearchChange={setSearchQuery}
 *   onFolderClick={handleFolderClick}
 *   onOnboardingClick={handleOnboardingClick}
 * />
 * ```
 */
export const OnboardingPickerTable: React.FC<OnboardingPickerTableProps> = ({
  items,
  loading,
  error,
  searchQuery,
  onSearchChange,
  onFolderClick,
  onOnboardingClick,
  selectedOnboardingId,
}) => {
  const theme = useTheme();

  // Handle row click based on item type
  const handleRowClick = (item: typeof items[number]) => {
    if (item.type === 'folder') {
      onFolderClick(item.id, item.name);
    } else {
      onOnboardingClick(item.id);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search Input */}
      <Box sx={{ mb: theme.spacing(2) }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search onboardings..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => onSearchChange('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: theme.spacing(2) }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: theme.spacing(4),
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && !error && items.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: theme.spacing(4),
          }}
        >
          <Typography color="text.secondary">
            {searchQuery
              ? `No results for "${searchQuery}"`
              : 'No onboardings found'}
          </Typography>
        </Box>
      )}

      {/* Table */}
      {!loading && !error && items.length > 0 && (
        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  Updated
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const isSelected =
                  item.type === 'onboarding' && item.id === selectedOnboardingId;

                return (
                  <TableRow
                    key={item.id}
                    hover
                    onClick={() => handleRowClick(item)}
                    data-selected={isSelected}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? theme.palette.action.selected
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? theme.palette.action.selected
                          : theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing(1),
                        }}
                      >
                        {item.type === 'folder' ? (
                          <FolderIcon
                            data-testid="FolderIcon"
                            sx={{ color: theme.palette.warning.main }}
                          />
                        ) : (
                          <DescriptionIcon
                            data-testid="DescriptionIcon"
                            sx={{ color: theme.palette.info.main }}
                          />
                        )}
                        <Typography variant="body2">{item.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(item.updatedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
